/**
 * `/v1/dashboard` in local mode (plan §6.6).
 *
 * Returns the FULL filtered row set, never a page. The dashboard's own tables
 * truncate for display, but risk score is normalised over the filtered set, so
 * truncating here would score the visible rows against themselves and produce a
 * different — and wrong — table than the live route (plan §6.6 sort-key
 * contract: no SQL limit may run before query-relative normalization).
 */
import type { DashboardPayload, DashboardQuery } from '@meridian/contracts';

import { computeBreadth } from '../../analytics/breadth';
import { aggregateSectors, toSectorAggregate } from '../../analytics/sector-aggregate';
import { DatasetNotFoundError } from '../../ports/dataset-repository';
import { narrowUniverse, unknownCompareTicker } from '../../selectors/filter-compare';
import { toWireSecurity, type DerivedSecurity } from '../../universe/derive-security';

import type { BookOwnership } from '../../selectors/filter-book';

export function buildDashboard(
  rows: readonly DerivedSecurity[],
  query: DashboardQuery,
  /* Optional, like `assigned` above: the filter is OFF when omitted, so every
     existing caller keeps its behaviour and only the repository opts in. */
  owned?: BookOwnership,
): DashboardPayload {
  /* Index ∩ compare ∩ book, composed, BEFORE anything derived. Sector
     aggregates, breadth and risk-score normalisation run over that pool, so
     narrowing after them would report whole-universe breadth beside a
     compare-set table. */
  const unknown = unknownCompareTicker(new Set(rows.map((row) => row.ticker)), query.compare);
  if (unknown !== undefined) throw new DatasetNotFoundError('security', unknown);
  const pool = narrowUniverse(rows, query.index, query.compare, owned);
  return {
    window: query.window,
    index: query.index,
    rows: pool.map(toWireSecurity),
    sectors: aggregateSectors(pool, query.window).map(toSectorAggregate),
    breadth: computeBreadth(pool, query.window),
  };
}
